import { FC, useEffect } from "react";

import { Stack } from "@mui/material";
import { CampaignAction } from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { useTranslate } from "../../../../../hooks/useTranslate";
import { FundraisingForm } from "../FundraisingForm";
import { FundraisingTypeOptions } from "../FundraisingTypeOptions";

import { useFundraiseDialog } from "./useFundraiseDialog";

import { DashboardInfobox } from "@/dashboard-design-system/Infobox/DashboardInfobox";
import { ThemedGeneralFundraiserProps } from "@/features/FormV2/sharedComponents/ThemedGeneralFundraiser";
import { ThemedIndividualFundraiserProps } from "@/features/FormV2/sharedComponents/ThemedIndividualFundraiser";
import { ThemedTeamFundraiserProps } from "@/features/FormV2/sharedComponents/ThemedTeamFundraiser";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormDialog, FormDialogProps } from "@/forms-design-system/FormDialog";
import { FormTypography } from "@/forms-design-system/FormTypography";

type CampaignType = { type: "general" | "team" | "individual" };
export type SearchedCampaign =
  | (ThemedGeneralFundraiserProps & CampaignType)
  | (ThemedTeamFundraiserProps & CampaignType)
  | (ThemedIndividualFundraiserProps & CampaignType);

export type FundraiseDialogProps = Pick<FormDialogProps, "open" | "onClose"> & {
  campaignId: string;
  allowTeamCreation: boolean;
  allowFundraiserCreation: boolean;
  allowJoinTeam: boolean;
  defaultTeamId?: string;
};

export const FundraiseDialog: FC<FundraiseDialogProps> = ({
  open,
  onClose,
  campaignId,
  allowTeamCreation,
  allowFundraiserCreation,
  allowJoinTeam,
  defaultTeamId,
}) => {
  const { t } = useTranslate();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const isPaidUser = query.paid === "true" && query.commandId;

  const {
    campaignAction,
    form,
    onSubmit,
    toggleCampaignAction,
    loading,
    hasTeam,
    submitIsDisabled,
    organizationCountry,
    defaultTarget,
  } = useFundraiseDialog({
    open: true,
    toggle: () => {},
    campaignId,
    defaultAction: defaultTeamId && allowJoinTeam ? CampaignAction.JoinTeam : CampaignAction.CreateTeam,
  });

  useEffect(() => {
    if (!open) {
      // Reset form data when dialog is closed
      form.reset();
    }
  }, [open, form]);

  // Handle close - prevent closing if user has paid registration
  const handleClose = () => {
    if (isPaidUser) {
      return;
    }
    onClose?.();
  };

  const actionButtons = [
    <FormButton
      key="fundraise-submit"
      variant="solid"
      onClick={onSubmit}
      disabled={submitIsDisabled}
      isLoading={loading}
      data-test="create-campaign-button"
    >
      {campaignAction === "CreateTeam"
        ? t("donationForm", "fundraiseDialog.button.createTeam")
        : campaignAction === "JoinTeam"
          ? t("donationForm", "fundraiseDialog.button.joinTeam")
          : t("donationForm", "fundraiseDialog.button.createCampaign")}
    </FormButton>,
    // Only show cancel button if user hasn't paid
    ...(isPaidUser
      ? []
      : [
          <FormButton key="fundraise-cancel" variant="outline" onClick={handleClose}>
            {t("donationForm", "fundraiseDialog.button.cancel")}
          </FormButton>,
        ]),
  ];

  const title = (
    <FormTypography variant="h6" id="toto">
      {t("donationForm", "fundraiseDialog.title")}
    </FormTypography>
  );

  return (
    <FormDialog
      title={title}
      open={open}
      onClose={handleClose}
      noPadding
      actions={actionButtons}
      maxWidth="sm"
      hideCloseButton={Boolean(isPaidUser)}
    >
      <form noValidate>
        <Stack sx={{ paddingInline: 3, paddingBlock: 2 }} spacing={2}>
          {/* Payment confirmation message for paid users */}
          {isPaidUser && (
            <DashboardInfobox vibe="positive" title={t("donationForm", "fundraiseDialog.paymentConfirmed")}>
              {t("donationForm", "fundraiseDialog.paymentConfirmedDescription")}
            </DashboardInfobox>
          )}
          {(allowTeamCreation || allowFundraiserCreation) && (
            <FundraisingTypeOptions
              campaignAction={campaignAction}
              toggleCampaignAction={toggleCampaignAction}
              allowTeamCreation={Boolean(allowTeamCreation)}
              allowFundraiserCreation={Boolean(allowFundraiserCreation)}
              allowJoinTeam={Boolean(allowJoinTeam)}
              hasTeam={hasTeam}
              showCreateTeamFirst
            />
          )}
          <FundraisingForm
            errors={form.formState.errors}
            register={form.register}
            control={form.control}
            setValue={form.setValue}
            campaignAction={campaignAction}
            organizationCountry={organizationCountry}
            campaignId={campaignId}
            defaultTarget={defaultTarget}
            defaultTeamId={defaultTeamId}
          />
        </Stack>
      </form>
    </FormDialog>
  );
};
